import React, { useEffect, useState } from 'react';
import { Form, Input, DatePicker, Switch, Button, Space, Select, Upload, InputNumber, Modal, message, Spin, Typography, Alert, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { authService, ROLE_LEVELS } from '../../../services/authService';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Constants
const phonePattern = /^1[3-9]\d{9}$/;
const namePattern = /^[\u4e00-\u9fa5]{2,10}$/;
const idCardPattern = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/;

const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/jpg', 'image/bmp',
  'image/webp', 'image/tiff', 'image/heic'
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const examOptions = [
  { label: '科目一', value: 'exam1' },
  { label: '科目二', value: 'exam2' },
  { label: '科目三', value: 'exam3' },
  { label: '科目四', value: 'exam4' },
];

const StudentModifyForm = ({ onSubmit, form, initialValues }) => {
  const [loading, setLoading] = useState(true);
  const [canEditFinance, setCanEditFinance] = useState(false);
  const [canEditManager, setCanEditManager] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [receiptFileList, setReceiptFileList] = useState([]);
  const [tuitionReceiptList, setTuitionReceiptList] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(initialValues?.paymentMethod || '未交款');
  const [isTransferred, setIsTransferred] = useState(initialValues?.transferred || false);
  const [isDocumented, setIsDocumented] = useState(initialValues?.documented || false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);

  useEffect(() => {
    const testPermissions = async () => {
      try {
        setLoading(true);
        const currentUser = authService.getCurrentUser();
        
        console.log('=== Permission Test Results ===');
        console.log('Current User:', {
          exists: !!currentUser,
          username: currentUser?.get('username'),
          id: currentUser?.id
        });

        if (currentUser) {
          // Get user roles first
          const roles = await authService.getUserRoles(currentUser);
          const userRoles = roles.map(role => ({
            name: role.get('name'),
            id: role.id,
            level: ROLE_LEVELS[role.get('name').toUpperCase()]
          }));
          console.log('User roles:', userRoles);

          // Get the highest role level
          const highestRoleLevel = Math.max(
            ...userRoles.map(role => role.level || 0)
          );
          console.log('Highest role level:', highestRoleLevel);

          // Set permissions based on role level
          const hasFinanceAccess = highestRoleLevel >= ROLE_LEVELS.FINANCE;
          const hasManagerAccess = highestRoleLevel >= ROLE_LEVELS.MANAGER;
          // Only allow editing if user is manager or above
          const canEditAccess = highestRoleLevel > ROLE_LEVELS.EMPLOYEE;

          console.log('Permission calculation:', {
            highestRoleLevel,
            ROLE_LEVELS,
            checks: {
              financeCheck: `${highestRoleLevel} >= ${ROLE_LEVELS.FINANCE} = ${hasFinanceAccess}`,
              managerCheck: `${highestRoleLevel} >= ${ROLE_LEVELS.MANAGER} = ${hasManagerAccess}`,
              canEdit: `${highestRoleLevel} > ${ROLE_LEVELS.EMPLOYEE} = ${canEditAccess}`
            }
          });

          setCanEditFinance(hasFinanceAccess);
          setCanEditManager(hasManagerAccess);
          setCanEdit(canEditAccess);
        }
      } catch (error) {
        console.error('Permission test error:', error);
        message.error('权限测试失败');
      } finally {
        setLoading(false);
      }
    };

    testPermissions();
  }, []);

  useEffect(() => {
    if (initialValues) {
      // Convert dates and exam dates
      const values = {
        ...initialValues,
        signUpDate: initialValues.signUpDate ? dayjs(initialValues.signUpDate) : undefined,
        subject1PassedAt: initialValues.subject1PassedAt ? dayjs(initialValues.subject1PassedAt) : undefined,
        subject2PassedAt: initialValues.subject2PassedAt ? dayjs(initialValues.subject2PassedAt) : undefined,
        subject3PassedAt: initialValues.subject3PassedAt ? dayjs(initialValues.subject3PassedAt) : undefined,
        subject4PassedAt: initialValues.subject4PassedAt ? dayjs(initialValues.subject4PassedAt) : undefined,
      };
      form.setFieldsValue(values);

      // Initialize receipt files from various sources
      let receiptFiles = [];
      
      // Try to get files from receiptPics array (from studentService.getReceiptPictures)
      if (initialValues.receiptPics && Array.isArray(initialValues.receiptPics)) {
        receiptFiles = initialValues.receiptPics.map((pic, index) => {
          const fileUrl = pic && pic.attributes && pic.attributes.url 
            ? pic.attributes.url
            : (pic && pic.url ? (typeof pic.url === 'function' ? pic.url() : pic.url) : null);
          
          if (fileUrl) {
            return {
              uid: `-${index + 1}`,
              name: `收据照片${index + 1}.jpg`,
              status: 'done',
              url: fileUrl,
              type: 'image/jpeg'
            };
          }
          return null;
        }).filter(Boolean);
      } 
      // Look for individual receiptPic fields if receiptPics array not available
      else {
        // Check for legacy receiptPic
        if (initialValues.receiptPic) {
          const fileUrl = initialValues.receiptPic.attributes && initialValues.receiptPic.attributes.url 
            ? initialValues.receiptPic.attributes.url
            : (initialValues.receiptPic.url ? (typeof initialValues.receiptPic.url === 'function' ? initialValues.receiptPic.url() : initialValues.receiptPic.url) : null);
          
          if (fileUrl) {
            receiptFiles.push({
              uid: '-1',
              name: '收据照片1.jpg',
              status: 'done',
              url: fileUrl,
              type: 'image/jpeg'
            });
          }
        }
        
        // Check for numbered receipt pic fields
        const count = initialValues.receiptPicCount || 0;
        for (let i = 1; i <= count; i++) {
          const picField = initialValues[`receiptPic${i}`];
          if (picField) {
            const fileUrl = picField.attributes && picField.attributes.url 
              ? picField.attributes.url
              : (picField.url ? (typeof picField.url === 'function' ? picField.url() : picField.url) : null);
            
            if (fileUrl) {
              receiptFiles.push({
                uid: `-${i}`,
                name: `收据照片${i}.jpg`,
                status: 'done',
                url: fileUrl,
                type: 'image/jpeg'
              });
            }
          }
        }
      }
      
      setReceiptFileList(receiptFiles);
      console.log('Initialized receipt files:', receiptFiles);

      // Initialize tuition receipt files from various sources
      let tuitionFiles = [];
      
      // Try to get files from tuitionReceipts array (from studentService.getTuitionReceipts)
      if (initialValues.tuitionReceipts && Array.isArray(initialValues.tuitionReceipts)) {
        tuitionFiles = initialValues.tuitionReceipts.map((receipt, index) => {
          const fileUrl = receipt && receipt.attributes && receipt.attributes.url 
            ? receipt.attributes.url
            : (receipt && receipt.url ? (typeof receipt.url === 'function' ? receipt.url() : receipt.url) : null);
          
          if (fileUrl) {
            return {
              uid: `-${index + 1}`,
              name: `学费收据${index + 1}.jpg`,
              status: 'done',
              url: fileUrl,
              type: 'image/jpeg'
            };
          }
          return null;
        }).filter(Boolean);
      } 
      // Look for individual tuitionReceipt fields if tuitionReceipts array not available
      else {
        // Check for legacy tuitionReceipt
        if (initialValues.tuitionReceipt) {
          const fileUrl = initialValues.tuitionReceipt.attributes && initialValues.tuitionReceipt.attributes.url 
            ? initialValues.tuitionReceipt.attributes.url
            : (initialValues.tuitionReceipt.url ? (typeof initialValues.tuitionReceipt.url === 'function' ? initialValues.tuitionReceipt.url() : initialValues.tuitionReceipt.url) : null);
          
          if (fileUrl) {
            tuitionFiles.push({
              uid: '-1',
              name: '学费收据1.jpg',
              status: 'done',
              url: fileUrl,
              type: 'image/jpeg'
            });
          }
        }
        
        // Check for numbered tuition receipt fields
        const count = initialValues.tuitionReceiptCount || 0;
        for (let i = 1; i <= count; i++) {
          const receiptField = initialValues[`tuitionReceipt${i}`];
          if (receiptField) {
            const fileUrl = receiptField.attributes && receiptField.attributes.url 
              ? receiptField.attributes.url
              : (receiptField.url ? (typeof receiptField.url === 'function' ? receiptField.url() : receiptField.url) : null);
            
            if (fileUrl) {
              tuitionFiles.push({
                uid: `-${i}`,
                name: `学费收据${i}.jpg`,
                status: 'done',
                url: fileUrl,
                type: 'image/jpeg'
              });
            }
          }
        }
      }
      
      setTuitionReceiptList(tuitionFiles);
      console.log('Initialized tuition receipt files:', tuitionFiles);

      setPaymentMethod(initialValues.paymentMethod || '未交款');
      setIsTransferred(initialValues.transferred || false);
    }
  }, [initialValues, form]);

  // Debug logs
  console.log('Component state:', {
    canEditFinance,
    canEditManager,
    loading,
    initialValues: !!initialValues
  });

  if (loading) {
    return <Spin tip="Loading..." />;
  }

  // If user is employee (no edit permission), show message
  if (!canEdit) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Alert
          message="权限不足"
          description="您没有修改学员信息的权限。需要主管或以上权限才能修改学员信息。"
          type="error"
          showIcon
        />
      </div>
    );
  }

  // File handling functions
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    console.log('Previewing file:', file);
    let previewUrl = file.url;
    
    if (!previewUrl && file.originFileObj) {
      previewUrl = await getBase64(file.originFileObj);
    }
    
    setPreviewImage(previewUrl);
    setPreviewOpen(true);
    setPreviewTitle(file.name || 'Image');
  };

  // New method to handle previewing multiple images
  const handlePreviewMultiple = (images, title) => {
    if (images && images.length > 0) {
      const urls = images.map(img => img.url || (img.originFileObj ? URL.createObjectURL(img.originFileObj) : null))
                         .filter(Boolean);
      setPreviewImages(urls);
      setPreviewTitle(title);
      setPreviewVisible(true);
    }
  };

  const beforeUpload = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error('只能上传图片文件!');
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error('图片大小不能超过 5MB!');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleReceiptChange = ({ fileList: newFileList }) => {
    console.log('Receipt files changed:', newFileList);
    setReceiptFileList(newFileList);
  };

  const handleTuitionReceiptChange = ({ fileList: newFileList }) => {
    console.log('Tuition receipt files changed:', newFileList);
    setTuitionReceiptList(newFileList);
  };

  // Handle form submission with multiple files
  const handleFormSubmit = async (values) => {
    try {
      // Process receipt files - only include new files for upload
      const newReceiptFiles = receiptFileList
        .filter(file => !!file.originFileObj)
        .map(file => file.originFileObj)
        .filter(Boolean);
      
      // Process tuition receipt files - only include new files for upload
      const newTuitionFiles = tuitionReceiptList
        .filter(file => !!file.originFileObj)
        .map(file => file.originFileObj)
        .filter(Boolean);
      
      // Prepare data for submission
      const formData = {
        ...values,
        receiptPic: newReceiptFiles.length > 0 ? newReceiptFiles : undefined,
        tuitionReceipt: newTuitionFiles.length > 0 ? newTuitionFiles : undefined
      };
      
      // Call the original onSubmit function
      await onSubmit(formData);
      
    } catch (error) {
      console.error('Form submission error:', error);
      message.error('提交失败: ' + error.message);
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>上传</div>
    </div>
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFormSubmit}
      initialValues={initialValues}
    >
      {/* Finance Level Fields */}
      <Form.Item
        name="name"
        label={
          <Space>
            姓名
            {!canEditFinance && <Text type="secondary">(需要财务权限)</Text>}
          </Space>
        }
        rules={[
          { required: true, message: '请输入姓名' },
          { pattern: namePattern, message: '请输入2-10个汉字' }
        ]}
      >
        <Input disabled={!canEditFinance} />
      </Form.Item>

      <Form.Item
        name="uniqueId"
        label={
          <Space>
            身份证号
            {!canEditFinance && <Text type="secondary">(需要财务权限)</Text>}
          </Space>
        }
        rules={[
          { required: true, message: '请输入身份证号' },
          { pattern: idCardPattern, message: '请输入正确的身份证号' }
        ]}
      >
        <Input disabled={!canEditFinance} />
      </Form.Item>

      <Form.Item 
        name="verified" 
        label={
          <Space>
            审核状态
            {!canEditFinance && <Text type="secondary">(需要财务权限)</Text>}
          </Space>
        }
        valuePropName="checked"
      >
        <Switch disabled={!canEditFinance} />
      </Form.Item>

      {/* Manager Level Fields */}
      <Form.Item 
        name="documented" 
        label={
          <Space>
            已建档
            {!canEditManager && <Text type="secondary">(需要主管权限)</Text>}
          </Space>
        }
        valuePropName="checked"
      >
        <Switch 
          disabled={!canEditManager} 
          onChange={setIsDocumented}
        />
      </Form.Item>

      {isDocumented && (
        <Form.Item
          name="fileId"
          label="档案号"
          rules={[
            { required: true, message: '请输入档案号' }
          ]}
        >
          <Input disabled={!canEditManager} />
        </Form.Item>
      )}

      {/* Payment method field */}
      <Form.Item
        name="paymentMethod"
        label={
          <Space>
            交款方式
            {!canEditManager && <Text type="secondary">(需要主管权限)</Text>}
          </Space>
        }
        rules={[{ required: true, message: '请选择交款方式' }]}
      >
        <Select onChange={setPaymentMethod} disabled={!canEditManager}>
          <Option value="未交款">未交款</Option>
          <Option value="现金">现金</Option>
          <Option value="微信">微信</Option>
          <Option value="支付宝">支付宝</Option>
        </Select>
      </Form.Item>

      {/* Receipt picture upload - always visible */}
      <Form.Item
        name="receiptPic"
        label={
          <Space>
            收据照片
            {!canEditManager && <Text type="secondary">(需要主管权限)</Text>}
          </Space>
        }
        extra="支持 .jpg, .jpeg, .png, .bmp, .webp, .tiff, .heic 格式，大小不超过 5MB，最多上传5张"
      >
        <div>
          <Upload
            listType="picture-card"
            fileList={receiptFileList}
            onPreview={handlePreview}
            onChange={handleReceiptChange}
            beforeUpload={beforeUpload}
            accept=".jpg,.jpeg,.png,.bmp,.webp,.tiff,.heic"
            disabled={!canEditManager}
            multiple={true}
            maxCount={5}
          >
            {receiptFileList.length >= 5 ? null : uploadButton}
          </Upload>
          
          {receiptFileList.length > 0 && (
            <Button 
              type="primary" 
              onClick={() => handlePreviewMultiple(receiptFileList, '收据照片')}
              style={{ marginTop: 8 }}
            >
              预览全部收据照片 ({receiptFileList.length}张)
            </Button>
          )}
        </div>
      </Form.Item>

      {/* Payment-related fields only shown when payment is not '未交款' */}
      {paymentMethod !== '未交款' && (
        <>
          <Form.Item
            name="tuitionFee"
            label="学费"
            rules={[{ required: true, message: '请输入学费金额' }]}
          >
            <InputNumber
              disabled={!canEditManager}
              style={{ width: '100%' }}
              formatter={value => `￥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/￥\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="receiptNumber"
            label="收据号"
            rules={[{ required: true, message: '请输入收据号' }]}
          >
            <Input disabled={!canEditManager} />
          </Form.Item>

          <Form.Item
            name="tuitionReceipt"
            label={
              <Space>
                学费收据
                {!canEditManager && <Text type="secondary">(需要主管权限)</Text>}
              </Space>
            }
            extra="支持 .jpg, .jpeg, .png, .bmp, .webp, .tiff, .heic 格式，大小不超过 5MB，最多上传5张"
          >
            <div>
              <Upload
                listType="picture-card"
                fileList={tuitionReceiptList}
                onPreview={handlePreview}
                onChange={handleTuitionReceiptChange}
                beforeUpload={beforeUpload}
                accept=".jpg,.jpeg,.png,.bmp,.webp,.tiff,.heic"
                disabled={!canEditManager}
                multiple={true}
                maxCount={5}
              >
                {tuitionReceiptList.length >= 5 ? null : uploadButton}
              </Upload>
              
              {tuitionReceiptList.length > 0 && (
                <Button 
                  type="primary" 
                  onClick={() => handlePreviewMultiple(tuitionReceiptList, '学费收据')}
                  style={{ marginTop: 8 }}
                >
                  预览全部学费收据 ({tuitionReceiptList.length}张)
                </Button>
              )}
            </div>
          </Form.Item>
        </>
      )}

      {/* Employee Level Fields - Editable by all levels */}
      <Form.Item
        name="phone"
        label="手机号"
        rules={[
          { required: true, message: '请输入手机号' },
          { pattern: phonePattern, message: '请输入正确的手机号' }
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item name="address" label="地址">
        <TextArea />
      </Form.Item>

      <Form.Item name="gender" label="性别">
        <Select>
          <Option value="男">男</Option>
          <Option value="女">女</Option>
        </Select>
      </Form.Item>

      <Form.Item name="ethnicity" label="民族">
        <Input />
      </Form.Item>

      <Form.Item
        name="signUpDate"
        label="报名日期"
        rules={[{ required: true, message: '请选择报名日期' }]}
      >
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name="VIP" label="VIP" valuePropName="checked">
        <Switch />
      </Form.Item>

      <Form.Item name="transferred" label="转入" valuePropName="checked">
        <Switch onChange={setIsTransferred} />
      </Form.Item>

      {isTransferred && (
        <>
          {examOptions.map(({ label, value }) => (
            <React.Fragment key={value}>
              <Form.Item
                name={`${value}Passed`}
                label={`${label}通过`}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name={`${value}PassedAt`}
                label={`${label}通过日期`}
                dependencies={[`${value}Passed`]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  disabled={!form.getFieldValue(`${value}Passed`)}
                />
              </Form.Item>
            </React.Fragment>
          ))}
        </>
      )}

      <Form.Item name="referrer" label="推荐人">
        <Input />
      </Form.Item>

      <Form.Item name="notes" label="备注">
        <TextArea rows={4} />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            保存
          </Button>
          <Button onClick={() => form.resetFields()}>
            重置
          </Button>
        </Space>
      </Form.Item>

      {/* Single image preview modal */}
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        <img 
          alt="preview" 
          style={{ width: '100%' }} 
          src={previewImage} 
        />
      </Modal>

      {/* Multiple images preview modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        width={800}
        onCancel={() => setPreviewVisible(false)}
      >
        <div style={{ marginTop: 16 }}>
          <Row gutter={[16, 16]}>
            {previewImages.map((src, index) => (
              <Col span={8} key={index}>
                <img
                  src={src}
                  alt={`${previewTitle} ${index + 1}`}
                  style={{ width: '100%', objectFit: 'cover', borderRadius: 4 }}
                />
              </Col>
            ))}
          </Row>
        </div>
      </Modal>
    </Form>
  );
};

StudentModifyForm.propTypes = {
  form: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default StudentModifyForm; 